import { Card, CardContent } from "@/components/ui/card";
import { Sparkline, type SparklineTone } from "@/components/cinch/Sparkline";
import { cn } from "@/lib/utils";

/**
 * Bigger KPI tile used in the Summary header. Renders an eyebrow label, a
 * large value, an optional secondary line (plan / Δ / target), and an
 * inline sparkline drawn from week-by-week locked snapshot data.
 *
 * Larger than KpiTile so the Summary tab reads from across the room — the
 * static HTML report Stan uses in the exec review keeps numbers heavy at
 * the top, and the live tool should match.
 */
/** One sub-number under the headline (e.g. New / Partner / CS). */
export interface KpiBreakdownItem {
  label: string;
  value: React.ReactNode;
}

export function BigKpiTile({
  label,
  value,
  sub,
  breakdown,
  tone = "brand",
  history,
  baseline,
  onOpen,
  openLabel,
  className,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  /**
   * Optional constituent numbers shown beneath the headline. Use when the
   * headline is a sum readers need decomposed (the Closed tile splits into
   * New / Partner / CS). Kept to a single row — 2–4 items.
   */
  breakdown?: readonly KpiBreakdownItem[];
  tone?: SparklineTone;
  /** Cents history — last N weeks; renders the sparkline beneath the value. */
  history?: readonly number[];
  /** Optional baseline (e.g. plan target) drawn as a dashed line. */
  baseline?: number;
  /**
   * Makes the whole tile a drill-down affordance. When set, the card becomes a
   * button so it's keyboard-reachable — the tile is the only way into the
   * detail drawer, so it can't be mouse-only.
   */
  onOpen?: () => void;
  /** Accessible name for the drill-down. Defaults to "Open {label} detail". */
  openLabel?: string;
  className?: string;
}) {
  const interactive = onOpen != null;
  return (
    <Card
      className={cn(
        "overflow-hidden",
        interactive &&
          "cursor-pointer transition-colors hover:border-power/40 hover:bg-muted/30 focus-within:border-power/40",
        className,
      )}
      {...(interactive
        ? {
            role: "button",
            tabIndex: 0,
            "aria-label": openLabel ?? `Open ${label} detail`,
            onClick: onOpen,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen?.();
              }
            },
          }
        : {})}
    >
      <CardContent className="space-y-2 p-4">
        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
        <div className="text-2xl font-semibold tabular-nums leading-tight md:text-3xl">
          {value}
        </div>
        {sub && (
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {sub}
          </div>
        )}
        {breakdown && breakdown.length > 0 && (
          <dl className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-1.5">
            {breakdown.map((b) => (
              <div key={b.label} className="min-w-0">
                <dt className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
                  {b.label}
                </dt>
                <dd className="text-xs font-semibold tabular-nums">{b.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {history && history.length > 1 && (
          <Sparkline
            points={history}
            tone={tone}
            baseline={baseline}
            ariaLabel={`${label} sparkline`}
          />
        )}
      </CardContent>
    </Card>
  );
}
